import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Logo } from '../components/Logo'

gsap.registerPlugin(ScrollTrigger)

export function Footer() {
  const rootRef = useRef<HTMLElement>(null)
  const isLive = import.meta.env.VITE_APPSTORE_LIVE === '1'
  const storeUrl = import.meta.env.VITE_APPSTORE_URL || ''
  const storeDirect = isLive && !!storeUrl

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = gsap.context(() => {
      gsap.from('.cta-headline, .cta-sub, .cta-buttons, .cta-badge', {
        opacity: 0,
        y: 30,
        duration: 1,
        ease: 'expo.out',
        stagger: 0.08,
        scrollTrigger: { trigger: root, start: 'top 75%' },
      })
    }, root)
    return () => ctx.revert()
  }, [])

  return (
    <footer
      id="download"
      ref={rootRef}
      className="relative px-6 md:px-16 pt-32 md:pt-44 pb-12 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 -z-10 h-[80%]"
        style={{
          background:
            'radial-gradient(60% 80% at 50% 0%, var(--accent-soft) 0%, transparent 70%)',
        }}
      />

      <div className="mx-auto max-w-[1100px]">
        {/* CTA */}
        <div className="text-center">
          <h2
            className="cta-headline font-display font-semibold text-ink-1"
            style={{
              fontSize: 'clamp(56px, 9vw, 144px)',
              letterSpacing: '-0.04em',
              lineHeight: 0.95,
            }}
          >
            Join the<br />
            <span
              style={{
                background:
                  'linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              first wave
            </span>
          </h2>

          <p className="cta-sub mt-8 max-w-[640px] mx-auto text-[15px] md:text-base text-ink-2 leading-relaxed">
            Live on the App Store. No account, no bank login, no servers. Drop in a transaction,
            keep a streak, see where your month is going.
          </p>

          <div className="cta-buttons mt-12 flex flex-wrap gap-3 justify-center">
            <a
              {...(storeDirect
                ? { href: storeUrl, target: '_blank' as const, rel: 'noopener noreferrer' as const }
                : { href: '/#beta' })}
              className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[var(--accent)] text-bg-0 text-base font-semibold transition-transform hover:-translate-y-0.5 shadow-[0_8px_30px_-8px_var(--accent-ring)]"
            >
              <AppleIcon />
              Get it on the App Store
            </a>
            {/* Product Hunt button hidden until the PH launch page is live —
                re-add the <a href="…/products/ruvi"> + <ProductHuntIcon /> when ready. */}
          </div>

          <div className="cta-badge mt-7 text-[12px] tracking-[2px] uppercase text-ink-3">
            iOS 26 · Free · Pro tier in v1.1
          </div>
        </div>

        {/* Made by */}
        <div className="mt-32 md:mt-44 pt-10 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-6">
          <a
            href="#top"
            aria-label="Back to top"
            className="group flex items-center gap-3 rounded-xl -m-1 p-1 transition-transform hover:-translate-y-0.5"
          >
            <Logo className="w-9 h-9 rounded-xl transition-transform duration-500 group-hover:rotate-[12deg]" />
            <div>
              <div className="font-display font-semibold text-base leading-none">Ruvi</div>
              <div className="text-[12px] text-ink-3 mt-1">ruvi.cash</div>
            </div>
          </a>

          <a
            href="https://nekit.xyz"
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-colors"
          >
            <span className="text-[13px] text-ink-2">
              Designed & built by{' '}
              <span className="text-ink-1 font-medium">nekit.xyz</span>
            </span>
            <span className="text-ink-2 group-hover:text-ink-1 transition-colors">
              <ArrowIcon />
            </span>
          </a>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-2 text-[12px] text-ink-3">
          <div>© 2026 Ruvi.cash — All rights reserved</div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:hi@ruvi.cash"
              className="hover:text-ink-1 transition-colors"
            >
              hi@ruvi.cash
            </a>
            <a href="/#privacy" className="hover:text-ink-1 transition-colors">
              Privacy
            </a>
            <a href="/#terms" className="hover:text-ink-1 transition-colors">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M11.4 8.5c0-1.6 1.3-2.4 1.3-2.4-.7-1-1.8-1.2-2.2-1.2-1-.1-1.9.5-2.4.5s-1.3-.5-2.1-.5c-1.1 0-2.1.6-2.7 1.6-1.1 2-.3 4.9.8 6.5.5.8 1.2 1.6 2 1.6s1.1-.5 2.1-.5 1.3.5 2.1.5 1.4-.8 2-1.6c.6-.9.9-1.8.9-1.8s-1.8-.7-1.8-2.7zm-1.6-5C10.3 2.9 10.7 2 10.6 1c-.8 0-1.8.6-2.3 1.2-.5.5-.9 1.4-.8 2.3.9.1 1.8-.5 2.3-1.1z" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M5 11L11 5M11 5H6M11 5V10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* Product Hunt brand mark — kept for when the PH launch button is re-added.
   Restore the <a> in the CTA row above and uncomment this.
function ProductHuntIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 40 40" aria-hidden>
      <circle cx="20" cy="20" r="20" fill="#DA552F" />
      <path
        fill="#fff"
        d="M22.667 20H17.333v-5.333h5.334a2.667 2.667 0 0 1 0 5.333zm0-9.333h-9.334v18.666h4v-5.333h5.334a6.667 6.667 0 0 0 0-13.333z"
      />
    </svg>
  )
}
*/
